import { BASE_URL } from 'config';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  Autocomplete,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import { UploadFile } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DataTable from "examples/Tables/DataTable";
import variantsTableData from "../data/variantsTableData";
import axios from "axios";
import Swal from "sweetalert2";

const AddProduct = ({ onClose, onSuccess, initialData }) => {
  const [product, setProduct] = useState({
    id: initialData?.id || null,
    title: initialData?.name || "",
    description: initialData?.description || "",
    category: initialData?.category_id || "",
    brand: initialData?.brand_id || "",
    price: initialData?.price || "",
    sku: initialData?.sku || "",
    discountPrice: initialData?.old_price || "",
    purchasePrice: initialData?.purchase_price || "",
    quantity: initialData?.quantity || "",
    minimumQuantity: initialData?.low_stock_quantity || "",
    weight: initialData?.weight || "",
    height: initialData?.height || "",
    width: initialData?.width || "",
    depth: initialData?.depth || "",
    tax: {
      applyTax: true,
      value: initialData?.tax || "",
      type: initialData?.type_tax || "percentage",
    },
    stock: {
      trackStock: true,
    },
    seo: {
      keywords: initialData?.keywords || "",
      slug: initialData?.slug || "",
      metaTitle: initialData?.meta_title || "",
      metaIcon: initialData?.meta_icon || "",
    },
    video: {
      provider: initialData?.video_provider || "",
      url: initialData?.video_provider_link || "",
    },
    status: initialData?.is_active ?? 1,
  });

  const [media, setMedia] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [stores, setStores] = useState([]);
  const [enableAddFile, setEnableAddFile] = useState(initialData?.is_receive_file || false);
  const [labelAddFile, setLabelAddFile] = useState(initialData?.label_receive_file || "");
  const [enableAddText, setEnableAddText] = useState(initialData?.is_receive_text || false);
  const [labelAddText, setLabelAddText] = useState(initialData?.label_receive_text || "");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!!initialData);
  const [quantityErrors, setQuantityErrors] = useState({});

  useEffect(() => {
    if (!initialData) {
      setLoading(false); // إذا لم يكن هناك بيانات تعديل، لا تظهر التحميل
      return;
    }
    setLoading(true); // فعّل التحميل عند وجود بيانات تعديل

    if (initialData.product_variants?.length > 0) {
      const variants = initialData.product_variants || [];
      const transformedVariants = variants.map((variant) => {
        const attrs = variant.product_attributes || [];
        return {
          combo: attrs.map((attr) => ({
            attribute_id: attr.attribute_value?.attribute_id,
            attribute_value_id: attr.attribute_value_id,
            value: attr.attribute_value?.value,
          })),
          id: variant.id,
          price: variant.price,
          discount: variant.old_price,
          purchasePrice: variant.purchase_price,
          quantity: variant.quantity,
          taxValue: variant.tax,
          taxType: variant.type_tax,
          weight: variant.weight,
          dimensions: {
            length: variant.length,
            width: variant.width,
            depth: variant.height,
          },
          sku: variant.sku,
          image: variant.image ? variant.image : null,
          active: variant.is_active === 1,
        };
      });
      setCombinations(transformedVariants);

      // استخراج السمات وقيمها لتظهر في القائمة المنسدلة
      const extractedAttributes = {};
      variants.forEach((variant) => {
        variant.product_attributes?.forEach((attr) => {
          const attrId = attr.attribute_value?.attribute_id;
          const attrName = attr.attribute_value?.attribute?.name;

          if (!extractedAttributes[attrId]) {
            extractedAttributes[attrId] = {
              name: String(attrId),
              label: attrName,
              values: [],
            };
          }

          // أضف كل القيم من كل المتغيرات مع تصفية التكرار بناءً على attribute_value_id
          if (
            !extractedAttributes[attrId].values.some(
              (v) => String(v.attribute_value_id) === String(attr.attribute_value_id)
            )
          ) {
            extractedAttributes[attrId].values.push({
              value: attr.attribute_value?.value,
              attribute_value_id: attr.attribute_value_id,
            });
          }
        });
      });

      // طباعة سمات المنتج وقيم السمات عند جلب بياناته للتعديل
      const attributesToPrint = Object.values(extractedAttributes);
      console.log("سمات المنتج (attributes) عند التعديل:", attributesToPrint);
      attributesToPrint.forEach((attr) => {
        console.log(`السمة: ${attr.label} (ID: ${attr.name})`);
        attr.values.forEach((val) => {
          console.log(`  - القيمة: ${val.value} (attribute_value_id: ${val.attribute_value_id})`);
        });
      });

      // تصفية القيم المكررة لكل سمة
      Object.keys(extractedAttributes).forEach((attrId) => {
        extractedAttributes[attrId].values = extractedAttributes[attrId].values.filter(
          (v, i, arr) =>
            arr.findIndex((x) => String(x.attribute_value_id) === String(v.attribute_value_id)) ===
            i
        );
      });

      const availableAttrs = Object.values(extractedAttributes).map((attr) => ({
        label: attr.label,
        value: attr.name,
        options: attr.values,
      }));

      setAvailableAttributes(availableAttrs);
      setAttributes(Object.values(extractedAttributes));
      setLoading(false); // أوقف التحميل بعد انتهاء المعالجة
    } else {
      // إذا لم يكن هناك متغيرات، أوقف التحميل بعد تجهيز البيانات الفارغة
      setAvailableAttributes([]);
      setAttributes([]);
      setCombinations([]);
      setLoading(false);
    }
  }, [initialData]);
  // إعدادات المحرر
  const [descriptionImages, setDescriptionImages] = useState([]);
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;

        // Add to state instead of uploading immediately
        setDescriptionImages((prev) => [...prev, { file, base64: base64Image }]);

        // Insert Base64 image into Quill
        const quill = window.quillRef;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", base64Image);
      };
      reader.readAsDataURL(file);
    };
  }, []);
  const uploadDescriptionImages = async (htmlContent, imagesToUpload) => {
    const token = localStorage.getItem("user-token");

    // رفع كل صورة واستبدالها بالرابط الجديد
    for (const { file, base64 } of imagesToUpload) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await axios.post(
          BASE_URL + "/save-file",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const imageUrl = response.data.url; // مثل: https://yourdomain.com/uploads/image.jpg
        console.log("image url from api : ", imageUrl);
        // استبدال فقط الصورة المطابقة في HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        const images = doc.querySelectorAll("img");
        for (const img of images) {
          if (img.src === base64) {
            img.src = imageUrl;
            break; // استبدل صورة واحدة فقط لكل دورة
          }
        }
        htmlContent = doc.body.innerHTML;
      } catch (error) {
        console.error("فشل في رفع الصورة:", error);
      }
    }
    console.log("htmle content new ", htmlContent);
    return htmlContent;
  };
  const handleImageDelete = (e) => {
    const target = e.target;

    // Make sure the clicked element is an image
    if (target.tagName === "IMG") {
      const quill = window.quillRef;
      const range = quill.getSelection();

      // Get current editor content
      let html = quill.root.innerHTML;

      // Convert HTML string to DOM
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Find the image in the DOM
      const images = doc.querySelectorAll("img");
      images.forEach((img) => {
        if (img.src === target.src) {
          img.remove(); // Remove the image
        }
      });

      // Update HTML content
      const updatedHtml = doc.body.innerHTML;

      // Set back to Quill editor
      quill.clipboard.dangerouslyPasteHTML(updatedHtml);
      quill.setSelection(range.index); // Keep cursor position
    }
  };
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline"],
          ["image", "video"],
          [{ list: "ordered" }, { list: "bullet" }],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [imageHandler]
  );

  // جلب بيانات المتاجر
  useEffect(() => {
    const fetchStores = async () => {
      const token = localStorage.getItem("user-token");
      try {
        const response = await fetch(BASE_URL + "/manager/stores", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setStores(data.data);
      } catch (error) {
        console.error("Error fetching Stores:", error);
      }
    };
    fetchStores();
  }, []);

  // دالة لجلب السمات
  const fetchAttributes = async () => {
    const token = localStorage.getItem("user-token");
    try {
      const response = await fetch(BASE_URL + "/manager/attributes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const transformedAttributes = data.data.map((attr) => ({
        label: attr.name,
        value: attr.id.toString(),
        options: attr.attribute_values.map((val) => ({
          id: val.id, // التأكد من وجود الـ ID
          value: val.value, // والقيمة النصية
        })),
      }));
      setAvailableAttributes(transformedAttributes);
    } catch (error) {
      console.error("فشل في جلب السمات:", error);
    }
  };
  //دالة لجلب بيانات الفئات
  useEffect(() => {
    const fetchCategories = async () => {
      const token = localStorage.getItem("user-token");
      try {
        const response = await fetch(BASE_URL + "/manager/categories", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setCategories(data.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);
  //دالة لجلب بيانات الماركات
  useEffect(() => {
    const fetchBrands = async () => {
      const token = localStorage.getItem("user-token");
      try {
        const response = await fetch(BASE_URL + "/manager/brands", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setBrands(data.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchBrands();
  }, []);
  useEffect(() => {
    fetchAttributes();
  }, []);
  useEffect(() => {
    if (initialData && initialData.medias?.length > 0) {
      // const baseUrl = "https://back.al-balad.sa/uploads/product_medias/file_names/";
      const existingMedia = initialData.medias.map((mediaItem) => ({
        id: mediaItem.id, // 👈 نضيف الـ ID هنا
        url: mediaItem.file_name,
        type: mediaItem.type === "image" ? "image" : "video",
        // isExisting: true, ← لن نحتاج إليه بعد الآن
      }));
      setMedia(existingMedia);
    } else {
      setMedia([]);
    }
  }, [initialData]);
  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files);
    setMedia((prev) => [...prev, ...files]);
  };
  const handleRemoveMedia = async (index) => {
    const itemToRemove = media[index];

    // إذا كان لديه id → يجب حذفه من السيرفر
    if (itemToRemove.id) {
      // عرض رسالة تأكيد قبل الحذف
      const willDelete = await Swal.fire({
        title: "هل أنت متأكد؟",
        text: "لن تتمكن من التراجع عن هذا!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "نعم، احذفه!",
        cancelButtonText: "إلغاء",
        customClass: {
          container: "swal2-zindex-high",
        },
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });

      if (!willDelete.isConfirmed) return;

      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.get(
          `${BASE_URL}/manager/product-medias/delete/ ${itemToRemove.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status) {
          setMedia((prev) => prev.filter((_, i) => i !== index));
          Swal.fire("تم الحذف!", "تم حذف الملف بنجاح.", "success");
        } else {
          Swal.fire("خطأ!", "فشل في حذف الملف من السيرفر.", "error");
        }
      } catch (error) {
        console.error("فشل في حذف الملف:", error);
        Swal.fire("خطأ!", "حدث خطأ أثناء محاولة الحذف.", "error");
      }
    } else {
      // ⚠️ ملف محلي جديد → نحذفه فقط من الحالة بدون أي رسائل
      setMedia((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));

    // إذا تم تغيير الكمية الرئيسية، حدث كمية أول متغير فقط
    if (name === "quantity") {
      setCombinations((prev) => {
        if (!prev || prev.length === 0) return prev;
        const newCombinations = [...prev];
        newCombinations[0] = {
          ...newCombinations[0],
          quantity: value === "" ? "0" : value,
        };
        return newCombinations;
      });
    }
  };

  const handleNestedChange = (section, field, value) => {
    setProduct((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleAddAttribute = () => {
    if (!selectedAttribute || !selectedValues.length) return;

    const attributeData = availableAttributes.find((attr) => attr.value === selectedAttribute);

    // فقط القيم التي لديها id
    const newValues = selectedValues
      .filter((selectedValue) => selectedValue.id)
      .map((selectedValue) => ({
        value: selectedValue.value,
        attribute_value_id: selectedValue.id,
      }));

    if (newValues.length === 0) {
      // أظهر رسالة تنبيه
      Swal.fire({
        icon: "warning",
        title: "تنبيه",
        text: "لا يمكن إضافة قيم سمات جديدة غير موجودة في النظام. الرجاء اختيار القيم من القائمة فقط.",
        confirmButtonText: "موافق",
      });
      return;
    }

    // تحقق إذا كانت السمة موجودة بالفعل
    const attrIndex = attributes.findIndex((attr) => attr.name === selectedAttribute);
    let newAttributes;
    if (attrIndex !== -1) {
      // السمة موجودة بالفعل، أضف القيم الجديدة فقط إذا لم تكن موجودة
      const existingAttr = attributes[attrIndex];
      const existingValues = existingAttr.values;
      // أضف القيم الجديدة فقط إذا لم تكن موجودة
      const mergedValues = [...existingValues];
      newValues.forEach((val) => {
        if (
          !existingValues.some(
            (v) => String(v.attribute_value_id) === String(val.attribute_value_id)
          )
        ) {
          mergedValues.push(val);
        }
      });
      // حدث السمة بقيمها الجديدة
      newAttributes = [...attributes];
      newAttributes[attrIndex] = {
        ...existingAttr,
        values: mergedValues,
      };
    } else {
      // السمة غير موجودة، أضفها مع القيم المختارة
      newAttributes = [
        ...attributes,
        {
          name: selectedAttribute,
          label: attributeData?.label,
          values: newValues,
        },
      ];
    }
    setAttributes(newAttributes);
    setSelectedValues([]);
    generateCombinations(newAttributes);
  };

  const generateCombinations = (attrs) => {
    const combine = (arrays, prefix = []) => {
      if (!arrays.length) return [prefix];
      return arrays[0].flatMap((value) => combine(arrays.slice(1), [...prefix, value]));
    };

    // تحويل السمات إلى مصفوفة تحتوي اسم السمة + قيمتها مع ID
    const arrays = attrs.map((attr) =>
      attr.values.map((valueObj) => ({
        attribute_id: parseInt(attr.name),
        attribute_value_id: valueObj.attribute_value_id,
        value: valueObj.value,
      }))
    );

    // توليد التوافيق
    const combined = combine(arrays).map((combo, idx) => ({
      combo,
      price: "0.0",
      discount: "0.0",
      purchasePrice: "0.0",
      quantity:
        idx === 0
          ? product.quantity !== undefined &&
            product.quantity !== "" &&
            product.quantity !== null &&
            product.quantity !== 0 &&
            product.quantity !== "0"
            ? product.quantity
            : "0"
          : "0.0",
      taxValue: "0.0",
      taxType: "percentage",
      dimensions: {
        length: "0.0",
        width: "0.0",
        depth: "0.0",
      },
      weight: "0.0",
      sku: "",
      image: null,
      active: false,
    }));

    setCombinations(combined);
  };

  const handleCombinationChange = (index, field, value) => {
    const updated = [...combinations];
    updated[index][field] = value;
    setCombinations(updated);
  };

  // دالة عند الخروج من حقل كمية متغير
  const handleQuantityBlur = (changedIndex, changedValue) => {
    const mainQuantity = Number(product.quantity) || 0;
    let newCombinations = [...combinations];
    let enteredQty = Number(changedValue) || 0;
    // إذا كان المتغير المعدل ليس الأول
    if (changedIndex !== 0) {
      let firstQty = Number(newCombinations[0].quantity) || 0;
      // كم يمكن إنقاصه من المتغير الأول
      const toDeduct = Math.min(firstQty, enteredQty);
      newCombinations[0] = {
        ...newCombinations[0],
        quantity: (firstQty - toDeduct).toString(),
      };
      // الكمية المدخلة تبقى كما هي في المتغير الحالي
      newCombinations[changedIndex] = {
        ...newCombinations[changedIndex],
        quantity: enteredQty.toString(),
      };
    } else {
      newCombinations[changedIndex] = {
        ...newCombinations[changedIndex],
        quantity: enteredQty.toString(),
      };
    }
    // احسب المجموع الجديد
    const total = newCombinations.reduce((sum, comb) => sum + (Number(comb.quantity) || 0), 0);
    if (mainQuantity > 0 && total > mainQuantity) {
      // تجاوز المجموع الكمية الرئيسية
      newCombinations[changedIndex] = {
        ...newCombinations[changedIndex],
        quantity: "0",
      };
      setCombinations(newCombinations);
      setQuantityErrors((prev) => ({ ...prev, [changedIndex]: "لقد تجاوزت كمية المنتج الرئيسية" }));
    } else {
      setCombinations(newCombinations);
      setQuantityErrors((prev) => {
        const updated = { ...prev };
        delete updated[changedIndex];
        return updated;
      });
    }
  };

  const { columns, rows } = variantsTableData(
    attributes,
    combinations,
    handleCombinationChange,
    handleQuantityBlur,
    quantityErrors
  );
  console.log("التركيبات : ", combinations);

  // دالة إضافة أو تعديل متغيرات المنتج وإرسال السمات المرتبطة بها
  const submitVariantsAndAttributes = async (productId, variants) => {
    const token = localStorage.getItem("user-token");

    // 1. جلب المتغيرات القديمة من السيرفر (عند التعديل فقط)
    let oldVariants = [];
    if (productId) {
      try {
        const response = await fetch(
          `${BASE_URL}/manager/product-variants/show-by-product-id/${productId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await response.json();
        oldVariants = data.data || [];
      } catch (error) {
        console.error("فشل في جلب المتغيرات القديمة:", error);
      }
    }

    // 2. حذف المتغيرات التي لم تعد موجودة في الواجهة
    const currentIds = variants.filter((v) => v.id).map((v) => v.id);
    const toDelete = oldVariants.filter((v) => !currentIds.includes(v.id));
    for (const variant of toDelete) {
      try {
        await axios.get(
          `${BASE_URL}/manager/product-variants/delete/${variant.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        console.error("فشل في حذف متغير:", error);
      }
    }

    // 3. إضافة أو تعديل المتغيرات الحالية
    for (const variant of variants) {
      const formData = new FormData();
      formData.append("product_id", productId);
      formData.append("sku", variant.sku);
      formData.append("purchase_price", parseFloat(variant.purchasePrice));
      formData.append("price", parseFloat(variant.price));
      formData.append("old_price", parseFloat(variant.discount));
      formData.append("quantity", parseInt(variant.quantity));
      formData.append("tax", parseFloat(variant.taxValue));
      formData.append("type_tax", variant.taxType);
      formData.append("weight", parseFloat(variant.weight));
      formData.append("length", parseFloat(variant.dimensions.length));
      formData.append("width", parseFloat(variant.dimensions.width));
      formData.append("height", parseFloat(variant.dimensions.depth));
      formData.append("is_active", variant.active ? 1 : 0);
      if (variant.image instanceof File) {
        formData.append("image", variant.image);
      }
      try {
        const endpoint = variant.id
          ? `${BASE_URL}/manager/product-variants/update/${variant.id}`
          : BASE_URL + "/manager/product-variants/store";
        const response = await axios.post(endpoint, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        const variantId = response.data.data.id;
        // إرسال السمات المرتبطة بهذا المتغير
        if (variant.combo && variant.combo.length > 0) {
          for (const item of variant.combo) {
            await axios.post(
              BASE_URL + "/manager/product-attributes/store",
              {
                product_variant_id: variantId,
                attribute_value_id: item.attribute_value_id,
                is_active: 1,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          }
        }
      } catch (error) {
        console.error("فشل في إرسال المتغير أو السمات:", error);
      }
    }
  };

  // دالة رفع صور ووسائط المنتج
  const submitMediaFiles = async (productId, mediaList) => {
    const token = localStorage.getItem("user-token");

    for (const file of mediaList) {
      // تحديد نوع الملف
      let fileType = "image";
      if (file.type && file.type.startsWith("video")) fileType = "video";

      // إذا كان الملف جديد (ليس له id)
      if (!file.id) {
        const formData = new FormData();
        formData.append("product_id", productId);
        formData.append("file_name", file); // نرسل الملف نفسه
        formData.append("type", fileType);
        formData.append("is_active", 1);
        try {
          const response = await axios.post(
            BASE_URL + "/manager/product-medias/store",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.data.status) {
            console.log("تم رفع الملف:", response.data);
          } else {
            console.error("فشل في رفع الملف:", response.data.message);
          }
        } catch (error) {
          console.error("خطأ في رفع الملف:", error);
        }
      }
    }
  };

  // دالة حفظ المنتج
  const handleSubmit = async () => {
    let newErrors = {};
    // التحقق من الحقول الأساسية للمنتج
    if (!product.title) newErrors.title = "يرجى إدخال عنوان المنتج";
    if (!product.category) newErrors.category = "يرجى اختيار الفئة";
    if (!product.price || parseFloat(product.price) <= 0)
      newErrors.price = "سعر المنتج يجب أن يكون أكبر من صفر";
    if (!product.quantity || parseInt(product.quantity) < 0)
      newErrors.quantity = "الكمية يجب أن تكون رقماً صحيحاً وليس سالباً";
    if (media.length === 0) newErrors.media = "يرجى رفع صورة واحدة على الأقل";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const formData = new FormData();
    Swal.fire({
      title: "جاري الحفظ...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
        const container = document.querySelector(".swal2-container");
        container.style.zIndex = "99999";
      },
    });
    let finalDescription = product.description;
    if (descriptionImages.length > 0) {
      finalDescription = await uploadDescriptionImages(product.description, descriptionImages);
    }
    setProduct((prev) => ({ ...prev, description: finalDescription }));
    formData.append("name", product.title);
    formData.append("category_id", product.category);
    formData.append("brand_id", product.brand || "");
    formData.append("sku", product.sku);
    formData.append("price", parseFloat(product.price));
    formData.append("old_price", product.discountPrice ? parseFloat(product.discountPrice) : 0);
    formData.append(
      "purchase_price",
      product.purchasePrice ? parseFloat(product.purchasePrice) : 0
    );
    formData.append("quantity", parseInt(product.quantity));
    formData.append("tax", product.tax.value ? parseFloat(product.tax.value) : 0);
    formData.append("type_tax", product.tax.type || "");
    formData.append("weight", product.weight ? parseFloat(product.weight) : 0);
    formData.append("length", product.length ? parseFloat(product.length) : 0);
    formData.append("height", product.height ? parseFloat(product.height) : 0);
    formData.append("width", product.width ? parseFloat(product.width) : 0);
    formData.append("keywords", product.seo.keywords || "");
    formData.append("description", finalDescription || "");
    formData.append("slug", product.seo.slug || "");
    formData.append("meta_title", product.seo.metaTitle || "");
    formData.append("video_provider", product.video.provider || "");
    formData.append("video_provider_link", product.video.url || "");
    formData.append("low_stock_quantity", parseInt(product.minimumQuantity) || 0);
    formData.append("is_receive_text", enableAddText ? 1 : 0);
    formData.append("is_receive_file", enableAddFile ? 1 : 0);
    formData.append("label_receive_text", labelAddText || "");
    formData.append("label_receive_file", labelAddFile || "");
    formData.append("is_active", product.status);
    try {
      const token = localStorage.getItem("user-token");
      const endpoint = product.id
        ? `${BASE_URL}/manager/products/update/${product.id}`
        : BASE_URL + "/manager/products/store";
      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.status) {
        const newProductId = response.data.data.id;
        if (combinations.length > 0) {
          await submitVariantsAndAttributes(newProductId, combinations);
        }
        if (media.length > 0) {
          await submitMediaFiles(newProductId, media);
        }
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        resetProductForm();
        return newProductId;
      } else {
        Swal.fire("خطأ!", response.data.message || "فشل في حفظ المنتج.", "error");
        return null;
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء  الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      return null;
    }
  };

  // دالة لتفريغ جميع الحقول بعد الحفظ
  const resetProductForm = () => {
    setProduct({
      id: null,
      title: "",
      description: "",
      category: "",
      brand: "",
      price: "",
      sku: "",
      discountPrice: "",
      purchasePrice: "",
      quantity: "",
      minimumQuantity: "",
      weight: "",
      height: "",
      width: "",
      depth: "",
      tax: {
        applyTax: true,
        value: "",
        type: "percentage",
      },
      stock: {
        trackStock: true,
      },
      seo: {
        keywords: "",
        slug: "",
        metaTitle: "",
        metaIcon: "",
      },
      video: {
        provider: "",
        url: "",
      },
      status: 1,
    });
    setMedia([]);
    setSelectedAttribute("");
    setSelectedValues([]);
    setAttributes([]);
    setCombinations([]);
    setEnableAddFile(false);
    setLabelAddFile("");
    setEnableAddText(false);
    setLabelAddText("");
    setErrors({});
    setQuantityErrors({});
    setDescriptionImages([]);
  };

  return (
    <Box sx={{ width: "100%", mx: "auto", p: 2 }}>
      <Backdrop open={loading} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container spacing={3}>
        {/* القسم الأول (المنتج) */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" mb={2}>
            إضافة منتج
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <TextField
              label="عنوان المنتج"
              fullWidth
              name="title"
              value={product.title}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title}
              sx={{ mb: 3 }}
            />
          </Box>

          <Typography variant="subtitle1" mb={1}>
            الوصف
          </Typography>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ height: "300px" }}>
              <ReactQuill
                theme="snow"
                value={product.description}
                onChange={(description) => setProduct((prev) => ({ ...prev, description }))}
                modules={quillModules}
                style={{ height: "200px" }}
                ref={(el) => {
                  if (el && el.getEditor) {
                    window.quillRef = el.getEditor();
                    // Attach click event for image deletion
                    const editorContainer = el.getEditor().root;
                    editorContainer.addEventListener("click", handleImageDelete);
                  }
                }}
              />
            </CardContent>
          </Card>

          <Typography variant="subtitle1" mb={1}>
            الوسائط
          </Typography>
          <Box sx={{ border: "2px dashed #ccc", p: 2, textAlign: "center", mb: 2 }}>
            <input
              type="file"
              id="upload-media"
              accept="image/*,video/*"
              multiple
              style={{ display: "none" }}
              onChange={handleMediaUpload}
            />
            <label htmlFor="upload-media">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadFile />}
                sx={{ px: 4, color: "white !important" }}
              >
                تحميل جديد
              </Button>
            </label>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
              {media.map((file, index) => {
                const url = file.id ? file.url : URL.createObjectURL(file);
                const isImage = file.type.startsWith("image");
                const isFirst = index === 0;
                return (
                  <Box
                    key={index}
                    sx={{
                      width: isFirst ? 150 : 80,
                      height: isFirst ? 150 : 80,
                      border: "1px solid #ccc",
                      overflow: "hidden",
                      borderRadius: 2,
                      position: "relative", // ← هذه الإضافة مهمة جداً
                    }}
                  >
                    {isImage ? (
                      <img
                        src={url}
                        alt={`media-${index}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <video
                        src={url}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        controls
                      />
                    )}
                    {/* زر الحذف */}
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => handleRemoveMedia(index)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0, // ← تغيير إلى اليسار بدلاً من اليمين
                        zIndex: 1,
                        minWidth: 0,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "white",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </Button>
                  </Box>
                );
              })}
            </Box>
            {errors.media && (
              <Typography color="error" variant="body2">
                {errors.media}
              </Typography>
            )}
          </Box>

          {/* الفئة والماركة */}
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.name || ""}
            value={
              Array.isArray(categories) ? categories.find((c) => c.id === product.category) : null
            }
            onChange={(e, newValue) =>
              handleChange({
                target: {
                  name: "category",
                  value: newValue ? newValue.id : "",
                },
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="اختر الفئة"
                error={!!errors.category}
                helperText={errors.category}
                fullWidth
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <Autocomplete
            options={brands}
            getOptionLabel={(option) => option.name || ""}
            value={Array.isArray(brands) ? brands.find((b) => b.id === product.brand) : null}
            onChange={(e, newValue) =>
              handleChange({
                target: {
                  name: "brand",
                  value: newValue ? newValue.id : "",
                },
              })
            }
            renderInput={(params) => <TextField {...params} label="اختر الماركة" fullWidth />}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* الأسعار */}
          <Card variant="outlined" sx={{ mt: 4, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" mb={3}>
                الأسعار
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  label="سعر المنتج"
                  type="number"
                  name="price"
                  fullWidth
                  value={product.price}
                  error={!!errors.price}
                  helperText={errors.price}
                  onChange={handleChange}
                />
                <TextField
                  label="السعر قبل الخصم"
                  type="number"
                  name="discountPrice"
                  fullWidth
                  value={product.discountPrice}
                  onChange={handleChange}
                />
              </Box>
              <TextField
                label="سعر الشراء"
                type="number"
                name="purchasePrice"
                fullWidth
                value={product.purchasePrice}
                onChange={handleChange}
              />
            </CardContent>
          </Card>

          {/* الضريبة */}
          <Card sx={{ mt: 4, p: 3 }}>
            <Typography variant="h6" mb={2}>
              الضريبة
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="قيمة الضريبة"
                  type="number"
                  value={product.tax.value}
                  onChange={(e) => handleNestedChange("tax", "value", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="tax-type-label">نوع الضريبة</InputLabel>
                  <Select
                    labelId="tax-type-label"
                    value={product.tax.type}
                    label="نوع الضريبة"
                    onChange={(e) => handleNestedChange("tax", "type", e.target.value)}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value="percentage">نسبة مئوية</MenuItem>
                    <MenuItem value="fixed">قيمة ثابتة</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Checkbox
                  checked={product.tax.applyTax}
                  onChange={(e) => handleNestedChange("tax", "applyTax", e.target.checked)}
                />
              }
              label="تطبيق الضريبة على المنتج"
              sx={{ mb: 3 }}
            />
          </Card>

          {/* المخزون */}
          <Card variant="outlined" sx={{ mt: 4, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" mb={3}>
                المخزون
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="الكمية المتوفرة"
                    type="number"
                    name="quantity"
                    fullWidth
                    value={product.quantity}
                    error={!!errors.quantity}
                    helperText={errors.quantity}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="أقل كمية"
                    type="number"
                    name="minimumQuantity"
                    fullWidth
                    value={product.minimumQuantity}
                    onChange={handleChange}
                    helperText="الحد الأدنى للمخزون"
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={product.stock.trackStock}
                    onChange={(e) => handleNestedChange("stock", "trackStock", e.target.checked)}
                  />
                }
                label="تتبع توفر المخزون"
              />
            </CardContent>
          </Card>

          {/* الشحن */}
          <Card variant="outlined" sx={{ mt: 4, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" mb={3}>
                الشحن
              </Typography>
              <TextField
                label="وزن المنتج (كجم)"
                type="number"
                name="weight"
                required
                inputProps={{ step: 1, min: 0 }}
                fullWidth
                value={product.weight}
                onChange={handleChange}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="الارتفاع (سم)"
                  type="number"
                  name="height"
                  required
                  inputProps={{ step: 1, min: 0 }}
                  fullWidth
                  value={product.height}
                  onChange={handleChange}
                />
                <TextField
                  label="العرض (سم)"
                  type="number"
                  name="width"
                  required
                  inputProps={{ step: 1, min: 0 }}
                  fullWidth
                  value={product.width}
                  onChange={handleChange}
                />
                <TextField
                  label="العمق (سم)"
                  type="number"
                  name="depth"
                  required
                  inputProps={{ step: 1, min: 0 }}
                  fullWidth
                  value={product.depth}
                  onChange={handleChange}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* القسم الثاني (التركيبات) */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" mb={2}>
            المتغيرات (Variants)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={5}>
              <Autocomplete
                options={availableAttributes}
                getOptionLabel={(option) => option.label || ""}
                value={
                  Array.isArray(availableAttributes)
                    ? availableAttributes.find((a) => a.value === selectedAttribute)
                    : null
                }
                onChange={(e, newValue) => setSelectedAttribute(newValue ? newValue.value : "")}
                renderInput={(params) => <TextField {...params} label="اختر السمة" fullWidth />}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid>
            <Grid item xs={5}>
              {selectedAttribute && (
                <Autocomplete
                  multiple
                  options={
                    Array.isArray(availableAttributes)
                      ? availableAttributes.find((a) => a.value === selectedAttribute)?.options ||
                        []
                      : []
                  }
                  getOptionLabel={(option) => option.value} // عرض القيمة النصية
                  value={selectedValues}
                  onChange={(event, newValue) => setSelectedValues(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id} // إضافة هذه الخاصية
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.value} {/* عرض القيمة النصية */}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="اختر القيم" placeholder="قيم السمة" />
                  )}
                  disableCloseOnSelect={true}
                />
              )}
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                onClick={handleAddAttribute}
                sx={{ px: 4, color: "white !important" }}
              >
                تطبيق
              </Button>
            </Grid>
          </Grid>

          <Box mt={3}>
            {attributes.map((attr) => (
              <Box key={attr.name} mb={1}>
                <Typography variant="subtitle2">{attr.label}:</Typography>
                {attr.values.map((val, idx) => (
                  <Chip key={idx} label={val.value} sx={{ m: 0.5 }} />
                ))}
              </Box>
            ))}
          </Box>

          {combinations.length > 0 && (
            <MDBox pt={3}>
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            </MDBox>
          )}

          {/* إعدادات مخصصة للعميل */}
          <Card sx={{ mt: 4, p: 3 }}>
            <Typography variant="h6" mb={2}>
              إعدادات مخصصة للعميل
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableAddFile}
                  onChange={(e) => setEnableAddFile(e.target.checked)}
                />
              }
              label="السماح للعميل بإضافة ملف"
            />
            {enableAddFile && (
              <Box mb={3} ml={3}>
                <TextField
                  fullWidth
                  label="ادخل النص"
                  value={labelAddFile}
                  onChange={(e) => setLabelAddFile(e.target.value)}
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="textSecondary" mt={1}>
                  النص الذي سيظهر للعميل عند إضافة ملف (مثال: "ارفع صورة التصميم").
                </Typography>
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={enableAddText}
                  onChange={(e) => setEnableAddText(e.target.checked)}
                />
              }
              label="السماح للعميل بإضافة نص"
            />
            {enableAddText && (
              <Box mb={2} ml={3}>
                <TextField
                  fullWidth
                  label="ادخل النص"
                  value={labelAddText}
                  onChange={(e) => setLabelAddText(e.target.value)}
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="textSecondary" mt={1}>
                  النص الذي سيظهر للعميل عند كتابة نص (مثال: "أدخل الاسم المطلوب طباعته").
                </Typography>
              </Box>
            )}
          </Card>

          {/* معلومات الفيديو */}
          <Card sx={{ mt: 4, p: 3 }}>
            <Typography variant="h6" mb={2}>
              مزود الفيديو
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="اسم مزود الفيديو"
                  placeholder="مثال: YouTube، Facebook"
                  value={product.video.provider}
                  onChange={(e) => handleNestedChange("video", "provider", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="رابط الفيديو"
                  placeholder="مثال: https://youtube.com/..."
                  value={product.video.url}
                  onChange={(e) => handleNestedChange("video", "url", e.target.value)}
                />
              </Grid>
            </Grid>
          </Card>

          {/* حالة المنتج */}
          <Card sx={{ mt: 4, p: 3, width: "50%" }}>
            <Typography variant="h6" mb={2}>
              حالة المنتج
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="product-status-label">اختر الحالة</InputLabel>
              <Select
                labelId="product-status-label"
                name="status"
                value={product.status}
                onChange={handleChange}
                label="اختر الحالة"
                sx={{ height: 40 }}
              >
                <MenuItem value={1}>نشط</MenuItem>
                <MenuItem value={0}>غير نشط</MenuItem>
              </Select>
            </FormControl>
          </Card>

          {/* SEO */}
          <Card sx={{ mt: 4, p: 3 }}>
            <Typography variant="h6" mb={2}>
              تحسين محركات البحث
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="كلمات لتحسين محركات البحث"
                  value={product.seo.keywords}
                  onChange={(e) => handleNestedChange("seo", "keywords", e.target.value)}
                  helperText="افصل بين الكلمات باستخدام الفاصلة"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="رابط المنتج (Slug)"
                  value={product.seo.slug}
                  onChange={(e) => handleNestedChange("seo", "slug", e.target.value)}
                  helperText="سيظهر هذا الرابط في عنوان URL للمنتج"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Meta Title"
                  value={product.seo.metaTitle}
                  onChange={(e) => handleNestedChange("seo", "metaTitle", e.target.value)}
                  helperText="العنوان الذي يظهر في نتائج محركات البحث"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ px: 4, color: "red !important" }}
                >
                  اختر أيقونة Meta
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleNestedChange("seo", "metaIcon", URL.createObjectURL(file));
                      }
                    }}
                  />
                </Button>
                {product.seo.metaIcon && (
                  <Box mt={2}>
                    <Typography variant="caption" display="block" mb={1}>
                      معاينة الأيقونة:
                    </Typography>
                    <Box
                      component="img"
                      src={product.seo.metaIcon}
                      alt="Meta Icon Preview"
                      sx={{ width: 64, height: 64, objectFit: "contain", borderRadius: 1 }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          * الحقول المطلوبة
        </Typography>

        <Box display="flex" gap={2}>
          {/* <Button variant="contained" color="error" onClick={handleCancel} sx={{ px: 4 }}>
            إلغاء
          </Button> */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            // disabled={Object.keys(errors).length > 0}
            sx={{ px: 4, color: "white !important" }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddProduct;
